import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import LoadingModal from './components/modals/LoadingModal';
import SaldoFndeModal from './components/modals/SaldoFndeModal';
import { API_BASE_URL } from '../config/ApiConfig';
import { fndeService } from './api/fndeService';
import Toolbar from './components/Toolbar';
import { mesesParaImportar } from './utils/mesesParaImportar';

const emptyForm = {
  ano: '',
  idPrograma: '',
  idEscola: '',
  idConta: '',
  cdb: '',
  poupanca: '',
  fundos: '',
  conta: '',
};

function keyOf(item) {
  return `${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}`;
}

function parseContaNumLabel(conta) {
  const s = String(conta || '').trim();
  const m = s.match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

function normalizeContaMatch(value) {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/^0+(?=[0-9A-Z])/g, '');

  return normalized || '0';
}

function getContaCadastroValue(conta) {
  return conta?.numConta || conta?.idConta || conta?.conta || '';
}

function parseMoneyBR(value) {
  if (value == null || value === '' || value === '-') return 0;
  if (typeof value === 'number') return value;
  const normalized = String(value)
    .replace(/\s/g, '')
    .replace(/[R$]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = Number(normalized);
  return Number.isNaN(n) ? 0 : n;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function SaldoFnde() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, contas } = useReferenceData(token, cnpj);

  const contasNormalizadas = useMemo(() => {
    return (contas || []).map(conta => ({
      original: conta,
      matchKey: normalizeContaMatch(getContaCadastroValue(conta)),
    }));
  }, [contas]);

  const sortedItems = useMemo(() => {
    const copy = Array.isArray(items) ? [...items] : [];
    copy.sort((a, b) => {
      const na = parseContaNumLabel(a.idConta);
      const nb = parseContaNumLabel(b.idConta);
      if (na !== nb) return na - nb;
      return String(a.idConta || '').localeCompare(String(b.idConta || ''), 'pt-BR', { sensitivity: 'base' });
    });
    return copy;
  }, [items]);

  const displayed = sortedItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, cdb: 0, poupanca: 0, fundos: 0, conta: 0, qtd: 0 };
      map[chave].cdb += Number(it.cdb || 0);
      map[chave].poupanca += Number(it.poupanca || 0);
      map[chave].fundos += Number(it.fundos || 0);
      map[chave].conta += Number(it.conta || 0);
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    if (!token || !cnpj || !ano) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    axios
      .get(`${API_BASE_URL.SALDO_FNDE}`, {
        params: { idEscola: cnpj, ano },
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        setItems([]);
        showDefaultModal('error', 'Erro ao carregar saldos FNDE.');
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function handleNew() {
    setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false });
    setShowModal(true);
  }

  function handleEdit() {
    if (selected.length !== 1) return;
    setForm({ ...selected[0], _isEditing: true });
    setShowModal(true);
  }

  function handleSave(e) {
    e.preventDefault();

    const payload = {
      ...form,
      ano: Number(ano),
      idEscola: cnpj,
      cdb: parseMoneyBR(form.cdb),
      poupanca: parseMoneyBR(form.poupanca),
      fundos: parseMoneyBR(form.fundos),
      conta: parseMoneyBR(form.conta),
    };

    axios
      .post(`${API_BASE_URL.SALDO_FNDE}`, payload, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      })
      .then(() => {
        setShowModal(false);
        setSelected([]);
        setAtualizar(prev => !prev);
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar saldo FNDE.'));
  }

  function handleDelete() {
    if (!selected.length) return;

    showConfirmModal('delete', () =>
      Promise.all(
        selected.map(s =>
          axios.delete(`${API_BASE_URL.SALDO_FNDE}`, {
            params: { ano: s.ano, idPrograma: s.idPrograma, idEscola: s.idEscola, idConta: s.idConta },
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          })
        )
      )
        .then(() => {
          setSelected([]);
          setAtualizar(prev => !prev);
        })
        .catch(() => showDefaultModal('error', 'Erro ao excluir saldo FNDE.'))
    );
  }

  async function handleImportarFnde() {
    if (!cnpj || !ano) return;

    setIsImporting(true);
    try {
      const anoBase = Number(ano);
      const dataAtual = new Date();
      const anosAlvo = mesesParaImportar(anoBase, dataAtual.getFullYear(), dataAtual.getMonth() + 1);

      let mesImportado = null;
      let resp = null;
      for (const mes of anosAlvo) {
        const alvo = `${mes}-${ano}`;
        resp = await fndeService.buscarSaldoEntidade(alvo, cnpj);
        if (!resp.data?.sucesso) {
          showDefaultModal('error', `Erro FNDE: ${resp.data?.erro || 'Falha na consulta'} (mês ${mes}/${ano})`);
          return;
        }
        const dadosAlvo = Array.isArray(resp.data?.dados) ? resp.data.dados : [];
        if (dadosAlvo.length) {
          mesImportado = mes;
          break;
        }
      }

      if (mesImportado === null) {
        showDefaultModal('warning', 'Nenhum dado retornado pelo FNDE para os meses consultados.');
        return;
      }

      const dados = Array.isArray(resp?.data?.dados) ? resp.data.dados : [];
      if (!dados.length) {
        showDefaultModal('warning', 'Nenhum dado retornado pelo FNDE para importação.');
        return;
      }

      const payload = [];
      const naoMapeadas = [];

      for (const row of dados) {
        const contaLabel = String(row['Conta'] || row['Número Conta'] || row['Num Conta'] || '').trim();
        if (!contaLabel) continue;

        const contaMatchKey = normalizeContaMatch(contaLabel);

        const contaObj = contasNormalizadas.find(c => c.matchKey === contaMatchKey)?.original;
        if (!contaObj || !contaObj.idPrograma) {
          naoMapeadas.push(contaLabel);
          continue;
        }

        payload.push({
          ano: Number(ano),
          idPrograma: contaObj.idPrograma,
          idEscola: cnpj,
          idConta: getContaCadastroValue(contaObj),
          cdb: parseMoneyBR(row['Saldo RDB/CDB']),
          poupanca: parseMoneyBR(row['Saldo Poupança'] || row['Saldo Poupana']),
          fundos: parseMoneyBR(row['Saldo Fundos']),
          conta: parseMoneyBR(row['Saldo Conta']),
        });
      }

      if (!payload.length) {
        showDefaultModal('warning', 'Nenhuma conta do FNDE corresponde às contas cadastradas para esta escola.');
        return;
      }

      await axios.post(`${API_BASE_URL.SALDO_FNDE}/lote`, payload, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      let mensagem = `${payload.length} registro(s) importado(s) de ${mesImportado}/${ano} e salvo(s) com sucesso.`;
      if (naoMapeadas.length) {
        mensagem += `\n${naoMapeadas.length} conta(s) não mapeada(s): ${naoMapeadas.slice(0, 10).join(', ')}`;
      }

      showDefaultModal('success', mensagem);
      setAtualizar(prev => !prev);
    } catch (error) {
      showDefaultModal('error', `Erro ao importar saldos FNDE: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  }

  function toggleSelect(item) {
    setSelected(prev => (prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]));
  }

  function programaLabel(idPrograma) {
    const p = (programas || []).find(x => String(x.id) === String(idPrograma));
    return p ? p.descricao : idPrograma;
  }

  function contaLabel(idConta) {
    const c = (contas || []).find(x => String(x.numConta) === String(idConta) || String(x.idConta) === String(idConta));
    return c ? (c.nomeConta || c.numConta || c.idConta) : idConta;
  }

  function totalLinha(item) {
    return Number(item.cdb || 0) + Number(item.poupanca || 0) + Number(item.fundos || 0) + Number(item.conta || 0);
  }

  return (
    <Layout>
      <LoadingModal show={isImporting} />
      <ResponsiveShadow>
        <PageHeader title="SALDO FNDE" icon="bi-bank" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <button className="btn btn-primary btn-sm botao-igual" onClick={handleImportarFnde} disabled={isImporting}>
              <i className="bi bi-cloud-download me-1"></i>Importar FNDE
            </button>
            <button className="btn btn-outline-info btn-sm botao-igual" onClick={() => navigate('/comparativo-sres-saldo-fnde')}>
              <i className="bi bi-bar-chart-line me-1"></i>Comparativo SRES x FNDE
            </button>
          </Toolbar>

          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={prog.idPrograma} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.idPrograma}>{programaLabel(prog.idPrograma)}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">RDB/CDB</span><span>{formatCurrency(prog.cdb)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Poupança</span><span>{formatCurrency(prog.poupanca)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Fundos</span><span>{formatCurrency(prog.fundos)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Conta</span><span>{formatCurrency(prog.conta)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.cdb + prog.poupanca + prog.fundos + prog.conta)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm"></span>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm" style={{ fontSize: '0.9rem' }}>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className="text-end">RDB/CDB</th>
                    <th className="text-end">Poupança</th>
                    <th className="text-end">Fundos</th>
                    <th className="text-end">Conta</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr
                      key={keyOf(item)}
                      className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''}
                      onClick={() => toggleSelect(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{setPagination.startIndex + idx + 1}</td>
                      <td>{programaLabel(item.idPrograma)}</td>
                      <td>{contaLabel(item.idConta)}</td>
                      <td className="text-end">{formatCurrency(item.cdb)}</td>
                      <td className="text-end">{formatCurrency(item.poupanca)}</td>
                      <td className="text-end">{formatCurrency(item.fundos)}</td>
                      <td className="text-end">{formatCurrency(item.conta)}</td>
                      <td className="text-end fw-bold text-success">{formatCurrency(totalLinha(item))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PageContent>

        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>

      <SaldoFndeModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        form={form}
        handleChange={handleChange}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}
